/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {
    ShopperCustomersMutations,
    useCustomerId,
    useCustomerProductLists,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from './use-current-customer'
import {useMemo} from 'react'
import {useMutation} from '@tanstack/react-query'
import {isServer} from '../utils/utils'
import logger from '../utils/logger-instance'

/**
 * Fetches the user's wish list. It is based on customers product lists with type 'wish_list'.
 *
 * Note it may return null if the user does not have a wish list.
 *
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @return {import("@tanstack/react-query").UseQueryResult}
 */
export const useWishList = (queryOptions) => {
    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer
    const {data: productLists, ...restOfQuery} = useCustomerProductLists(
        {
            parameters: {customerId}
        },
        {
            ...queryOptions,
            enabled: queryOptions?.enabled !== false && !isServer && Boolean(customerId)
        }
    )

    const wishList = useMemo(
        () => productLists?.data?.find((list) => list.type === 'wish_list') ?? null,
        [productLists]
    )

    return {
        data: wishList,
        ...restOfQuery
    }
}

/**
 * Checks if a product is in the user's wish list
 * @param productId
 * @return {boolean}
 */
export const useIsProductInWishlist = (productId) => {
    const {data: wishlist} = useWishList()
    return useMemo(
        () =>
            Boolean(
                productId &&
                    wishlist?.customerProductListItems?.find((i) => i.productId === productId)
            ),
        [wishlist, productId]
    )
}

/**
 * Adds a product to user's wish list if not already in the wish list.
 *
 * @param {import("@tanstack/react-query").UseMutationOptions} [mutationOptions]
 * @return {import("@tanstack/react-query").UseMutationResult}
 */
export const useAddProductToWishList = (mutationOptions) => {
    const customerId = useCustomerId()
    const {data: wishlist} = useWishList()
    const createCustomerProductList = useShopperCustomersMutation(
        ShopperCustomersMutations.CreateCustomerProductList
    )
    const createCustomerProductListItemMutation = useShopperCustomersMutation(
        ShopperCustomersMutations.CreateCustomerProductListItem
    )
    return useMutation({
        mutationFn: async (productId) => {
            if (!productId) {
                throw new Error('productId is required')
            }

            if (!customerId) {
                throw new Error('customerId is required')
            }

            const body = {
                quantity: 1,
                productId,
                public: false,
                priority: 1,
                type: 'product'
            }

            if (wishlist) {
                // Check if the item is already in the wish list
                const existingItem = wishlist?.customerProductListItems?.find(
                    (i) => i.productId === productId
                )

                if (existingItem) {
                    return existingItem
                }

                return await createCustomerProductListItemMutation.mutateAsync({
                    parameters: {
                        listId: wishlist.id,
                        customerId
                    },
                    body
                })
            } else {
                // Create a wish list
                const data = await createCustomerProductList.mutateAsync({
                    parameters: {customerId},
                    body: {
                        type: 'wish_list',
                        public: false
                    }
                })

                if (!data || !data.id) {
                    throw Error('Something is wrong. Please try again')
                }

                // add the item to the wish list
                return await createCustomerProductListItemMutation.mutateAsync({
                    parameters: {
                        listId: data.id,
                        customerId
                    },
                    body
                })
            }
        },
        ...mutationOptions
    })
}

/**
 * Checks if wishlist has products
 * @return {boolean}
 */
export const useHasWishlistProducts = () => {
    const {data: wishlist} = useWishList()
    return useMemo(() => Boolean(wishlist?.customerProductListItems?.length > 0), [wishlist])
}

export const useRemoveProductFromWishList = (mutationOptions) => {
    const customerId = useCustomerId()
    const {data: wishlist} = useWishList({
        enabled: true
    })
    const deleteCustomerProductListItemMutation = useShopperCustomersMutation(
        ShopperCustomersMutations.DeleteCustomerProductListItem
    )

    return useMutation({
        mutationFn: async (productId) => {
            if (!productId) {
                throw new Error('productId is required')
            }

            if (!customerId) {
                throw new Error('customerId is required')
            }

            // without wishlist we can't remove an item
            if (!wishlist) {
                return
            }

            const existingItem = wishlist?.customerProductListItems?.find(
                (i) => i.productId === productId
            )

            // if the item is not in the wish list, we don't need to do anything
            if (!existingItem) {
                return
            }

            return await deleteCustomerProductListItemMutation.mutateAsync({
                parameters: {
                    listId: wishlist.id,
                    customerId,
                    itemId: existingItem.id
                }
            })
        },
        ...mutationOptions
    })
}

export const useMergeWishlistMutation = (queryOptions, mutationOptions) => {
    const customerId = useCustomerId()
    const createCustomerProductList = useShopperCustomersMutation(
        ShopperCustomersMutations.CreateCustomerProductList
    )
    const createCustomerProductListItemMutation = useShopperCustomersMutation(
        ShopperCustomersMutations.CreateCustomerProductListItem
    )
    const customerProductListsQuery = useCustomerProductLists(
        {
            parameters: {customerId}
        },
        {
            ...queryOptions,
            enabled: queryOptions?.enabled !== false && !isServer && Boolean(customerId)
        }
    )

    return useMutation({
        mutationFn: async (wishlistProductIds) => {
            if (!customerId) {
                throw new Error('customerId is required')
            }
            if (!wishlistProductIds) {
                throw new Error('product ids are required')
            }
            const {data: productLists} = await customerProductListsQuery.refetch({
                throwOnError: true
            })
            const wishlist = productLists?.data?.find((list) => list.type === 'wish_list')

            let listId
            if (!wishlist) {
                // Create a wish list
                const data = await createCustomerProductList.mutateAsync({
                    parameters: {customerId},
                    body: {
                        type: 'wish_list',
                        public: false
                    }
                })

                if (!data || !data.id) {
                    throw Error('Something is wrong. Please try again')
                }
                listId = data.id
            } else {
                listId = wishlist.id
            }
            let hasNewItem = false
            for (const productId of wishlistProductIds) {
                // Check if the item is already in the wish list
                const existingItem = wishlist?.customerProductListItems?.find(
                    (i) => i.productId === productId
                )
                if (!existingItem) {
                    hasNewItem = true
                    const body = {
                        quantity: 1,
                        productId,
                        public: false,
                        priority: 1,
                        type: 'product'
                    }
                    try {
                        await createCustomerProductListItemMutation.mutateAsync({
                            parameters: {
                                listId: listId,
                                customerId
                            },
                            body
                        })
                    } catch (e) {
                        // log errors from adding to wishlist - i.e. unavailable product
                        // and allow to resume with the other wishlist items
                        logger.error(`Error adding product "${productId}" to wishlist`, e)
                    }
                }
            }
            return hasNewItem
        },
        ...mutationOptions
    })
}
